/**
 * The signed relay list. N-08, architecture Section 8, D-05.
 *
 * ## What this is
 *
 * Sync needs somewhere to put sealed records. The project runs relay #1, but it
 * is **entry one in a list built for many**, never "the" relay — the client
 * treats a one-entry list and a ten-entry list identically, so when other people
 * run relays nothing in the architecture changes and the project's relay stops
 * being a chokepoint.
 *
 * ## Why it is signed
 *
 * A relay cannot read sealed records, but it does see IP addresses, write timing
 * and record sizes (T-08, T-09). Substituting the list is therefore a real
 * attack: it would not reveal contents, but it would tell an attacker who is
 * using Thana and when. So the list carries an ECDSA P-256 signature and the
 * verification key is pinned into the build. A list that fails verification is
 * refused and the last good one is kept.
 *
 * ECDSA P-256 rather than Ed25519 only because WebCrypto support for Ed25519 is
 * still uneven across the browsers in NFR-17's matrix. Nothing else recommends it.
 *
 * ## Where it is fetched from, and what refreshing it can actually change
 *
 * The app's own origin, `/relays.json`, so fetching it adds no host to the CSP —
 * `connect-src 'self'` already covers it. A copy is bundled at build time as the
 * fallback and the last verified list is persisted, so sync works offline and on
 * first run.
 *
 * **What a refresh cannot do is change the set of peers.** That was claimed too
 * broadly at first and is worth stating precisely, because the difference matters
 * to what the app can promise:
 *
 *  - It cannot ADD a peer. CSP would refuse the connection (`connect-src` is fixed
 *    at build time from `relays.source.json`) and the Gun instance was configured
 *    without it.
 *  - It cannot fully REMOVE one either. Gun's `opt({ peers })` adds rather than
 *    replaces, so a peer already connected stays connected until the page reloads
 *    — and the build-time configuration would reinstate it anyway.
 *
 * So the peer set is bounded at build time and changing it needs a release, in
 * either direction. What a refresh genuinely governs:
 *
 *  - which relays the user is SHOWN and consents to,
 *  - their operator, jurisdiction and contact metadata,
 *  - the order and health of the rotation.
 *
 * That is narrower than "refreshable" suggests. The alternative — `connect-src
 * wss:` and an unbounded peer set — would trade a real guarantee for a
 * convenience, and for a static PWA a release is cheap.
 */
import { fromBase64 } from './crypto'
import { openDb, promisify, STORE, txDone } from './db'

export const RELAY_LIST_FORMAT = 'thana-relay-list'
export const RELAY_LIST_URL = '/relays.json'
const STORED_KEY = 'relayList'

/**
 * The verification key, pinned at build time.
 *
 * Base64 SPKI of an ECDSA P-256 public key. Pinned rather than fetched, because a
 * key that arrives with the list it verifies proves nothing.
 *
 * Changing this invalidates every existing list, so it is fixed once shipped —
 * the same rule as `GUN_ROOT` (ADR-0002). The private half never enters the
 * repository; see `scripts/sign-relay-list.mjs`.
 */
export const RELAY_LIST_PUBLIC_KEY: string =
  (import.meta.env?.VITE_RELAY_LIST_KEY as string | undefined)?.trim() ?? ''

export interface RelayEntry {
  /** WebSocket or HTTPS endpoint the Gun layer connects to. */
  url: string
  /** Who runs it. Displayed, so the user can decide whether to trust them. */
  operator: string
  /**
   * ISO 3166-1 alpha-2 country code of the hosting.
   *
   * Shown in the UI because it determines whose legal process can reach the
   * IP addresses and timing the relay observes. A user in a hostile jurisdiction
   * has a real interest in choosing.
   */
  jurisdiction: string
  /** How to reach the operator. Required: an anonymous relay is not trustworthy. */
  contact: string
}

export interface RelayManifest {
  format: typeof RELAY_LIST_FORMAT
  version: number
  issuedAt: string
  relays: RelayEntry[]
}

export interface SignedRelayList {
  manifest: RelayManifest
  /** Base64 ECDSA P-256 / SHA-256 signature over the canonical manifest. */
  signature: string
}

/**
 * Deterministic JSON, so signer and verifier agree byte for byte.
 *
 * Written here rather than borrowed from the Gun layer's `canonical.ts`: this
 * has to match what an offline signing script produces, and a signature format
 * whose definition lives in another module's private helper is a signature format
 * waiting to break. Ten lines is the cheaper dependency.
 */
export function canonicalise(value: unknown): string {
  if (value === null || typeof value !== 'object') return JSON.stringify(value)
  if (Array.isArray(value)) return `[${value.map(canonicalise).join(',')}]`
  const entries = Object.entries(value as Record<string, unknown>)
    .filter(([, v]) => v !== undefined)
    .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
  return `{${entries.map(([k, v]) => `${JSON.stringify(k)}:${canonicalise(v)}`).join(',')}}`
}

async function importVerificationKey(base64: string): Promise<CryptoKey> {
  return crypto.subtle.importKey(
    'spki',
    fromBase64(base64) as unknown as BufferSource,
    { name: 'ECDSA', namedCurve: 'P-256' },
    false,
    ['verify'],
  )
}

/**
 * Check a signed list.
 *
 * Returns the manifest, or undefined. Never throws for a bad list: a failed
 * verification is an expected condition — a corrupted download, a tampered file —
 * and the caller's job is to keep the last good list rather than to crash.
 */
export async function verifyRelayList(
  signed: unknown,
  publicKey: string = RELAY_LIST_PUBLIC_KEY,
): Promise<RelayManifest | undefined> {
  if (publicKey === '') return undefined
  if (typeof signed !== 'object' || signed === null) return undefined

  const candidate = signed as Partial<SignedRelayList>
  const manifest = candidate.manifest
  if (manifest?.format !== RELAY_LIST_FORMAT) return undefined
  if (!Array.isArray(manifest.relays)) return undefined
  if (typeof candidate.signature !== 'string') return undefined

  // Every field is required. A relay with no operator or no contact is not
  // something a user can make an informed decision about, so it is not a valid
  // entry rather than a partially valid one.
  const wellFormed = manifest.relays.every(
    (relay) =>
      typeof relay?.url === 'string' &&
      /^(wss|https):\/\//.test(relay.url) &&
      typeof relay.operator === 'string' &&
      relay.operator !== '' &&
      typeof relay.jurisdiction === 'string' &&
      typeof relay.contact === 'string' &&
      relay.contact !== '',
  )
  if (!wellFormed) return undefined

  try {
    const key = await importVerificationKey(publicKey)
    const ok = await crypto.subtle.verify(
      { name: 'ECDSA', hash: 'SHA-256' },
      key,
      fromBase64(candidate.signature) as unknown as BufferSource,
      new TextEncoder().encode(canonicalise(manifest)),
    )
    return ok ? manifest : undefined
  } catch {
    return undefined
  }
}

async function readStored(): Promise<RelayManifest | undefined> {
  const db = await openDb()
  const tx = db.transaction(STORE.meta, 'readonly')
  return promisify<RelayManifest | undefined>(tx.objectStore(STORE.meta).get(STORED_KEY))
}

async function writeStored(manifest: RelayManifest): Promise<void> {
  const db = await openDb()
  const tx = db.transaction(STORE.meta, 'readwrite')
  tx.objectStore(STORE.meta).put(manifest, STORED_KEY)
  await txDone(tx)
}

export interface LoadRelaysResult {
  relays: RelayEntry[]
  /** Where the list came from, for the sync status display (FR-507). */
  source: 'refreshed' | 'stored' | 'none'
  /** True when a fetched list existed but failed verification. */
  rejected: boolean
}

export interface LoadRelaysOptions {
  /** Test seam. Production passes nothing and uses the global. */
  fetch?: typeof fetch
  /**
   * Test seam. Production uses the key pinned at build time.
   *
   * Deliberately an override rather than a required argument: the pinned key IS
   * the security property, so the safe thing must be what happens when a caller
   * says nothing.
   */
  publicKey?: string
}

/**
 * Load the relay list, refreshing from the origin when possible.
 *
 * Order matters: a *verified* refresh replaces the stored list; anything else
 * leaves it alone. So a tampered or truncated file cannot downgrade a user who
 * already has a good list, and being offline is not a failure.
 */
export async function loadRelays(options: LoadRelaysOptions = {}): Promise<LoadRelaysResult> {
  const fetchImpl = options.fetch ?? fetch
  const publicKey = options.publicKey ?? RELAY_LIST_PUBLIC_KEY
  let rejected = false

  try {
    const response = await fetchImpl(RELAY_LIST_URL, { cache: 'no-cache' })
    if (response.ok) {
      const verified = await verifyRelayList(await response.json(), publicKey)
      if (verified !== undefined) {
        await writeStored(verified)
        return { relays: verified.relays, source: 'refreshed', rejected: false }
      }
      // A file was served and did not verify. Worth surfacing rather than
      // silently falling back — it means either a mistake or an attempt.
      rejected = true
    }
  } catch {
    // Offline, or no file. Neither is an error: the stored list is the answer.
  }

  const stored = await readStored()
  if (stored !== undefined) return { relays: stored.relays, source: 'stored', rejected }
  return { relays: [], source: 'none', rejected }
}

/**
 * Peer health, so one bad relay does not stall sync.
 *
 * In memory only. A relay being unreachable is usually transient and persisting
 * a verdict about it across sessions would turn a blip into a permanent
 * exclusion.
 */
const failures = new Map<string, number>()

/** How many consecutive failures before a relay is dropped from rotation. */
export const FAILURE_THRESHOLD = 3

export function recordRelayFailure(url: string): void {
  failures.set(url, (failures.get(url) ?? 0) + 1)
}

export function recordRelaySuccess(url: string): void {
  failures.delete(url)
}

export const isRelayHealthy = (url: string): boolean =>
  (failures.get(url) ?? 0) < FAILURE_THRESHOLD

/**
 * The relays worth trying, healthy ones first.
 *
 * Unhealthy relays are kept at the end rather than removed: if every relay has
 * failed — which usually means the device is offline, not that every operator is
 * down — returning an empty list would make recovery impossible.
 */
export function rotation(relays: readonly RelayEntry[]): RelayEntry[] {
  const healthy = relays.filter((relay) => isRelayHealthy(relay.url))
  const unhealthy = relays.filter((relay) => !isRelayHealthy(relay.url))
  return [...healthy, ...unhealthy]
}

/** Test seam. Not used by application code. */
export function resetRelayHealthForTests(): void {
  failures.clear()
}
